import { PageDto } from '@asset-metrix/portal-gateway-client-js';

import {
  parseComparisonDiscriminatorValues,
  stringifyComparisonEntityPool,
} from './comparisonDetailPageEntities';
import {
  COMPARISON_ENTITIES_URL_PARAM,
  DETAILS_PAGE_URL_DISCRIMINATOR_PARAM,
} from './constants';
import { getDetailsFromSearchParams } from './getDetailsFromSearchParams';

export type DetailPageNavigationIdentity = {
  detailsDiscriminator: string;
  detailsValue: string;
  hiddenFilterKeys: string[];
  persistedSearchParams: URLSearchParams;
  signature: string;
};

export function getDetailPageNavigationIdentity(
  searchParams: URLSearchParams,
  pageData?: PageDto,
  options?: { isCurrentPage?: boolean }
): DetailPageNavigationIdentity | null {
  const { isCurrentPage = false } = options ?? {};

  if (pageData?.type !== 'detailPage') {
    return null;
  }

  const { detailsDiscriminator, detailsValue } =
    getDetailsFromSearchParams(searchParams);

  if (detailsDiscriminator == null || detailsValue == null) {
    return null;
  }

  const hiddenFilterKeys = [
    ...new Set(
      (pageData.pageFilter?.filters ?? [])
        .filter((filter) => filter.hidden === true)
        .map((filter) => filter.key)
    ),
  ].sort();

  const isComparisonPage = searchParams.has(COMPARISON_ENTITIES_URL_PARAM);

  if (
    isCurrentPage &&
    !isComparisonPage &&
    !hiddenFilterKeys.includes(detailsDiscriminator)
  ) {
    throw new Error(
      `Invalid detail page filter configuration: discriminator key "${detailsDiscriminator}" must be configured as a hidden page filter.`
    );
  }

  const persistedSearchParams = new URLSearchParams();
  persistedSearchParams.set(
    DETAILS_PAGE_URL_DISCRIMINATOR_PARAM,
    detailsDiscriminator
  );

  for (const key of hiddenFilterKeys) {
    const values = [
      ...new Set(searchParams.getAll(key).filter(Boolean)),
    ].sort();
    for (const value of values) {
      persistedSearchParams.append(key, value);
    }
  }

  // For comparison pages, discriminator key is not hidden. Nav hrefs include the
  // selected pair; the cache-key signature uses only the entity pool so changing
  // the pair does not remount the page or re-initialize filter state.
  const cacheKeySearchParams = new URLSearchParams();
  cacheKeySearchParams.set(
    DETAILS_PAGE_URL_DISCRIMINATOR_PARAM,
    detailsDiscriminator
  );

  for (const key of hiddenFilterKeys) {
    const values = [
      ...new Set(searchParams.getAll(key).filter(Boolean)),
    ].sort();
    for (const value of values) {
      cacheKeySearchParams.append(key, value);
    }
  }

  if (isComparisonPage && !hiddenFilterKeys.includes(detailsDiscriminator)) {
    const unique = parseComparisonDiscriminatorValues(
      searchParams,
      detailsDiscriminator
    );
    for (const value of unique) {
      persistedSearchParams.append(detailsDiscriminator, value);
    }

    const dpEntities = stringifyComparisonEntityPool(searchParams);
    if (dpEntities !== '') {
      persistedSearchParams.set(COMPARISON_ENTITIES_URL_PARAM, dpEntities);
      cacheKeySearchParams.set(COMPARISON_ENTITIES_URL_PARAM, dpEntities);
    }
  }

  return {
    detailsDiscriminator,
    detailsValue,
    hiddenFilterKeys,
    persistedSearchParams,
    signature: isComparisonPage
      ? cacheKeySearchParams.toString()
      : persistedSearchParams.toString(),
  };
}

export function matchesPersistedDetailPageIdentity(
  storedSearchParams: URLSearchParams,
  currentSearchParams: URLSearchParams
): boolean {
  const storedEntries = [...storedSearchParams.entries()];

  if (storedEntries.length === 0) {
    return false;
  }

  const storedKeys = [...new Set(storedEntries.map(([key]) => key))].sort();

  return storedKeys.every((key) => {
    const storedValues = [...new Set(storedSearchParams.getAll(key))].sort();
    const currentValues = [...new Set(currentSearchParams.getAll(key))].sort();

    return (
      storedValues.length === currentValues.length &&
      storedValues.every((val, idx) => val === currentValues[idx])
    );
  });
}
